import { FC } from 'react';
import { Button, Container } from 'components/common';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { getLastAffordabilityCalcPage } from 'utils/localStorage';
import { CalculatorHeader } from '../CalculatorHeader';
import { CalculatorPages } from '../types';
import { AffordabilityCalculatorForm } from '../types';
import { ApplicantIncomeFooter } from './ApplicantIncomeFooter';
import { CalculatorLayoutProps } from './types';
import { getFormKeyBySectionName, pagesNamesByOrder } from './helper';
import { ApplicantsSidebar } from './ApplicantsSidebar';
import { getApplicantNumber } from '../ApplicantsWrapper/helpers';

export const CalculatorLayout: FC<CalculatorLayoutProps> = ({ children, currentSectionNumber, additionalClasses }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentSection = searchParams.get('section') as CalculatorPages;
  const isApplicantDetailsSection = currentSection === CalculatorPages.applicantDetails;
  const calculatorPagesTotalAmount = Object.keys(CalculatorPages).length;
  const applicantNumber = getApplicantNumber(searchParams);

  const getIsCurrentPageFilled = (pageName: CalculatorPages) => {
    const lastSavedPage = getLastAffordabilityCalcPage();
    const lastSavedPageIndex = Object.values(pagesNamesByOrder).findIndex(page => page === lastSavedPage);
    const pageIndex = Object.values(pagesNamesByOrder).findIndex(pageNameByOrder => pageNameByOrder === pageName);

    return pageIndex <= lastSavedPageIndex;
  };

  const sectionRedirectHandler = async (sectionName: CalculatorPages, isDisabled: boolean) => {
    if (currentSection !== CalculatorPages.reviewAnswers) {
      const sectionKey = getFormKeyBySectionName(currentSection);

      if (sectionKey !== null) {
        const triggerResultPromise = form.trigger(sectionKey, { shouldFocus: true });
        const isErrors = (await triggerResultPromise) === false;

        if (isDisabled || isErrors) return;
      } else {
        console.error('sectionKey is null', currentSection);
      }

      if (currentSection === sectionName) return;
    }
    !isDisabled && setSearchParams({ section: sectionName });
  };

  const sectionNames = Object.entries(pagesNamesByOrder);
  const form = useFormContext<AffordabilityCalculatorForm>();
  const formFields = form.watch();
  const numberOfApplicants = formFields.applicants.length;

  return (
    <div>
      <CalculatorHeader />
      <Container maxWidth={'lg'} className={'md:py-12'}>
        <div className={'flex justify-between'}>
          <div className={'xs:w-full md:w-7/12'}>
            <p className={'xs:hidden text-ash font-satoshiMedium my-0 font-medium sm:text-base md:block lg:text-lg'}>
              <FormattedMessage
                id={'affordabilityCalculator.layout.sectionNumber'}
                values={{
                  index: currentSectionNumber,
                  amount: calculatorPagesTotalAmount,
                }}
              />
            </p>
            <h1
              className={`text-main font-satoshiBlack border-light after:bg-main xs:text-1.5xl xs:pb-2 relative mt-4 border-2 border-x-0 border-t-0 border-solid font-black after:absolute after:-bottom-0.5 after:left-0 after:h-0.5 after:content-[''] md:mb-8 md:pb-5 lg:text-4xl ${additionalClasses}`}
            >
              <FormattedMessage id={`affordabilityCalculator.sections.${currentSection}`} />
            </h1>
            <p className={'text-ash font-satoshiMedium my-0 mb-3 text-sm font-medium md:hidden'}>
              <FormattedMessage
                id={'affordabilityCalculator.layout.sectionNumber'}
                values={{
                  index: currentSectionNumber,
                  amount: calculatorPagesTotalAmount,
                }}
              />
            </p>
            {currentSection === CalculatorPages.applicantDetails && numberOfApplicants > 1 && (
              <div className={'md:hidden'}>
                <h2 className={'text-dark font-satoshiBlack mb-3 mt-0 text-xl font-black'}>
                  Applicant {applicantNumber + 1}
                </h2>
                <ApplicantsSidebar />
              </div>
            )}
            {children}
          </div>
          <div className={'xs:hidden mt-0 min-h-full md:block md:w-4/12'}>
            <h3 className={'font-satoshiBlack text-main text-1.5xl font-black'}>
              <FormattedMessage id={'affordabilityCalculator.title'} />
            </h3>
            <div
              className={`after:bg-dust relative mt-2 after:absolute after:left-4 after:top-8 after:h-full after:w-px after:content-['']`}
            >
              {sectionNames.map(([sectionNumber, sectionName]) => {
                const isActive = sectionName === currentSection;
                const isPageFilled = getIsCurrentPageFilled(sectionName);
                const isAccessible = Number(sectionNumber) <= currentSectionNumber || isPageFilled;
                const isDisabled = isPageFilled ? false : currentSectionNumber < Number(sectionNumber);

                const defaultStateClasses = ' bg-white hover:bg-main hover:text-white';
                const activeStateClasses = ' bg-main text-white hover:bg-white hover:text-main cursor-pointer ';
                const disabledStateClasses = ' cursor-not-allowed bg-white hover:bg-white ';
                const accessibleClasses = ' bg-white border-fog text-dark/[0.1] hover:bg-paper';
                const elementStateClasses = isActive
                  ? activeStateClasses
                  : isDisabled
                  ? disabledStateClasses
                  : isAccessible
                  ? accessibleClasses
                  : defaultStateClasses;

                return (
                  <div key={sectionName} className={'mb-6'}>
                    <div
                      className={`flex items-center`}
                      onClick={() => sectionRedirectHandler(sectionName, isDisabled)}
                    >
                      <Button
                        type={'button'}
                        className={`border-light z-10 flex h-8 w-8 min-w-0 items-center justify-center rounded-full border border-solid px-0 ${elementStateClasses}`}
                      >
                        <span className={'font-satoshiBlack font-black'}>{sectionNumber}</span>
                      </Button>
                      <p
                        className={`${
                          isAccessible && 'cursor-pointer'
                        } font-satoshiBlack text-ash my-0 ml-4 font-black`}
                      >
                        <FormattedMessage id={`affordabilityCalculator.sections.${sectionName}`} />
                      </p>
                    </div>
                    {sectionName === CalculatorPages.applicantDetails && numberOfApplicants > 0 && (
                      <ApplicantsSidebar className={'ml-12 -mt-2'} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Container>
      {isApplicantDetailsSection && <ApplicantIncomeFooter />}
    </div>
  );
};
